import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../../store';
import {
  fetchWorkerStatistics,
  fetchWorkerShiftHistory,
} from '../../../../../modules/worker';
import dayjs from 'dayjs';
import CustomPagination from '../../../../../components/CustomPagination';
import CustomTableHeader from '../../../../../components/CustomHeader';
import {
  Box,
  Grid,
  Typography,
  TableRow,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import {
  InfoCard,
  IconWrapper,
  StatusChip,
  StyledTableContainer,
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  PaginationFooter
} from './styles';

interface Column {
  id: string;
  label: string;
  numeric?: boolean;
  disableSorting?: boolean;
}

const SHIFT_HISTORY_COLUMNS: Column[] = [
  { id: 'no', label: 'No.', numeric: false, disableSorting: true },
  { id: 'shift_start_time', label: 'Date', numeric: false, disableSorting: false },
  { id: 'facility_name', label: 'Facility Name', numeric: false, disableSorting: false },
  { id: 'location', label: 'Location', numeric: false, disableSorting: true },
  { id: 'start_time', label: 'Shift Time (Start)', numeric: false, disableSorting: true },
  { id: 'end_time', label: 'Shift Time (End)', numeric: false, disableSorting: true },
  { id: 'total_hours', label: 'Total Hours', numeric: false, disableSorting: true },
  { id: 'status', label: 'Status', numeric: false, disableSorting: true },
  { id: 'break_duration', label: 'Break Taken', numeric: false, disableSorting: true },
  { id: 'amount_paid', label: 'Service Total', numeric: false, disableSorting: true },
];



interface ShiftHistoryProps {
  workerId: number;
}

const ShiftHistory: React.FC<ShiftHistoryProps> = ({ workerId }) => {
  const dispatch: AppDispatch = useDispatch();

  const { data: statistics } = useSelector((state: RootState) => state.workerStatistics);
  const { data: shiftHistory, count } = useSelector((state: RootState) => state.workerShiftHistory);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('shift_start_time');

  useEffect(() => {
    if (workerId) {
      dispatch(fetchWorkerStatistics(workerId) as any);
      dispatch(fetchWorkerShiftHistory({
        workerId,
        page: page,
        pageSize: rowsPerPage,
        ordering: order === 'desc' ? `-${orderBy}` : orderBy
      }) as any);
    }
  }, [dispatch, workerId, page, rowsPerPage, order, orderBy]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Hours Worked
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics?.total_hours_worked ?? '-'} <Typography component="span" variant="body1" color="text.secondary">hours</Typography>
              </Typography>
            </Box>
            <IconWrapper bgcolor="#E8F5E9" color="#2E7D32">
              <PersonOutlineIcon />
            </IconWrapper>
          </InfoCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Attendance Rate
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics?.attendance_rate ?? '-'}%
              </Typography>
            </Box>
            <IconWrapper bgcolor="#E3F2FD" color="#1976D2">
              <GroupsOutlinedIcon />
            </IconWrapper>
          </InfoCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No-show
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics?.no_show ?? '-'} <Typography component="span" variant="body1" color="text.secondary">incidents</Typography>
              </Typography>
            </Box>
            <IconWrapper bgcolor="#FFEBEE" color="#D32F2F">
              <PersonOffOutlinedIcon />
            </IconWrapper>
          </InfoCard>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InfoCard>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Rest Compliance
              </Typography>
              <Typography variant="h4" fontWeight="bold">
                {statistics?.rest_compliance ?? '-'}%
              </Typography>
            </Box>
            <IconWrapper bgcolor="#FFF3E0" color="#F57C00">
              <MoreHorizOutlinedIcon />
            </IconWrapper>
          </InfoCard>
        </Grid>
      </Grid>

      {/* Table */}
      <StyledTableContainer>
        <StyledTable stickyHeader>
          <CustomTableHeader
            columns={SHIFT_HISTORY_COLUMNS}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <StyledTableBody>
            {shiftHistory?.results?.map((row, index) => (
              <TableRow
                key={index}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <StyledTableCell component="th" scope="row">{index + 1 + ((page - 1) * rowsPerPage)}</StyledTableCell>
                <StyledTableCell>{dayjs(row.shift_start_time).format('DD/MM/YYYY')}</StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 500 }}>{row.facility_name}</StyledTableCell>
                <StyledTableCell>{row.location}</StyledTableCell>
                <StyledTableCell>{dayjs(row.shift_start_time).format('HH:mm')}</StyledTableCell>
                <StyledTableCell>{dayjs(row.shift_end_time).format('HH:mm')}</StyledTableCell>
                <StyledTableCell>{row.total_hours}</StyledTableCell>
                <StyledTableCell>
                  <StatusChip status={row.status}>
                    {row.status}
                  </StatusChip>
                </StyledTableCell>
                <StyledTableCell>{row.break_duration || '-'}</StyledTableCell>
                <StyledTableCell>${row.amount_paid}</StyledTableCell>
              </TableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledTableContainer>

      <PaginationFooter>
        <Box>
          Showing {shiftHistory?.results?.length || 0} of {count} results
        </Box>
        <CustomPagination
          totalPages={Math.ceil((count || 0) / rowsPerPage)}
          currentPage={page}
          onPageChange={handlePageChange}
        />
      </PaginationFooter>
    </Box>
  );
};

export default ShiftHistory;
